import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStore } from "app/actions";
import {
  addContainer,
  fetchContainers,
  removeContainer,
  updateContainer,
} from "app/thunks/containersAPI";
import { Container } from "interfaces/Containers";
import { handleTableDataSorting, SortProps } from "utils/table/table";

import { RootState } from "../../app/store";

export interface State {
  list: Container[];
  isFetching: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: any;
}

const initialState: State = {
  list: [],
  isLoading: false,
  isSuccess: false,
  isError: false,
  isFetching: false,
  error: undefined,
};

export const containersSlice = createSlice({
  name: "containers",
  initialState,
  reducers: {
    sortContainers: (state, action: PayloadAction<SortProps>) => {
      const { sortBy, sortOrder } = action.payload;
      if (state.list.length > 0 && sortBy && sortOrder) {
        const sorted = [...state.list];
        sorted.sort(
          handleTableDataSorting<Container>({
            sortBy,
            sortOrder,
            dateProperties: ["createdAt"],
          }),
        );
        state.list = sorted;
      }
    },
    resetFlags: (state) => {
      state.isSuccess = false;
      state.isLoading = false;
      state.isError = false;
      state.isFetching = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetStore, () => initialState)
      .addCase(fetchContainers.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchContainers.rejected, (state, action) => {
        state.isError = true;
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(
        fetchContainers.fulfilled,
        (state, action: PayloadAction<Container[]>) => {
          state.isSuccess = true;
          state.isFetching = false;
          state.list = action.payload;
        },
      )
      .addCase(addContainer.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addContainer.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(
        addContainer.fulfilled,
        (state, action: PayloadAction<Container>) => {
          state.isSuccess = true;
          state.isLoading = false;
          state.list.push(action.payload);
        },
      )
      .addCase(updateContainer.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateContainer.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(
        updateContainer.fulfilled,
        (state, action: PayloadAction<Container>) => {
          state.isSuccess = true;
          state.isLoading = false;

          const { payload } = action;

          const containerToUpdate = state.list.find(
            (container) => container._id === payload._id,
          );
          if (containerToUpdate) {
            // containerToUpdate.tabletId = payload.tabletId;
            containerToUpdate.isActive = payload.isActive;
            containerToUpdate.deviceId = payload.deviceId;
          }
        },
      )
      .addCase(removeContainer.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(removeContainer.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(removeContainer.fulfilled, (state, action) => {
        state.isSuccess = true;
        state.isLoading = false;

        state.list = state.list.filter(
          (container) => container._id !== action.payload._id,
        );
      });
  },
});

export const { sortContainers, resetFlags } = containersSlice.actions;

// ------ Selectors ------
export const getContainers = (state: RootState) => ({
  containers: state.containers.list,
  isFetching: state.containers.isFetching,
});

export const getContainerActionState = (state: RootState) => ({
  isLoading: state.containers.isLoading,
  isSuccess: state.containers.isSuccess,
  isError: state.containers.isError,
  error: state.containers.error,
});

export default containersSlice.reducer;
